import moment from 'moment';
import { parse } from 'date-fns';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Ref,
} from 'react';
import { Typography } from '@tempmee/react';
import { Center } from '~/commonComponents/Center';
import { DateContent } from '~/components/Request/multiday/components/Rows/DateRow/DateContent';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { useToggle } from '~/hooks';

import { OutlineWarning01Icon, SolidCalendarIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Field } from '../Field';
import { useDateRemoval } from './hooks';
import { IDateProps } from './Types';
import { CustomDateValue } from './Value';

const EMPTY_DATE_ARRAY: Date[] = [];

const MaximumDateExceededPopupStyle = {
  WarningIconWrapper: { marginBottom: 20 },
};
const ConfirmationPopupStyle = {
  Description: { width: '95%', marginBottom: 50 },
  LeftButton: { backgroundColor: colors.secondary[500] },
};

const Component = (
  {
    defaultValue = [],
    excluded = [],
    isDisabled = false,
    isOpen,
    value: controlledValue,
    onChange,
    onToggle,
  }: IDateProps,
  ref: Ref<HTMLDivElement>,
) => {
  const [internalValue, setInternalValue] = useState<Date[]>(defaultValue);

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  const fmtCurrentValueStr = useMemo(
    () => currentValue.map((it) => moment(it).format('YYYY-MM-DD')),
    [currentValue],
  );

  const wasControlledRef = useRef(isControlled);
  useEffect(() => {
    if (!import.meta.env.PROD && wasControlledRef.current !== isControlled) {
      console.warn(
        `Warning: Date component is changing from ${wasControlledRef.current ? 'controlled' : 'uncontrolled'} to ${isControlled ? 'controlled' : 'uncontrolled'}. ` +
          'Elements should not switch from controlled to uncontrolled (or vice versa).',
      );
    }
    wasControlledRef.current = isControlled;
  }, [isControlled]);

  const doUpdateValue = useCallback(
    (newValue: Date[]) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    },
    [isControlled, onChange],
  );

  const handleRemoveDate = useCallback(
    (index: number) => {
      const newValue = currentValue.filter((_, i) => i !== index);
      doUpdateValue(newValue);
    },
    [currentValue, doUpdateValue],
  );

  const controllerDateRemoval = useDateRemoval({
    onRemoveDate: handleRemoveDate,
  });
  const [isShowLimitExceededWarning, controlsLimitExceeded] = useToggle(false);

  const displayValues = isOpen ? EMPTY_DATE_ARRAY : currentValue;

  const handleConfirmDate = useCallback(
    (values: string[]) => {
      onToggle?.();
      // Parse date strings in local timezone instead of UTC to prevent timezone offset issues
      const dates = values.map((it) => parse(it, 'yyyy-MM-dd', new globalThis.Date()));
      doUpdateValue(dates.sort((a, b) => a.getTime() - b.getTime()));
    },
    [doUpdateValue, onToggle],
  );

  const DateValueComponent = useCallback(
    () => (
      <CustomDateValue
        isDisabled={isDisabled}
        values={displayValues}
        onAddDatesClick={onToggle}
        onRemoveDate={controllerDateRemoval.handleRemoveDate}
      />
    ),
    [isDisabled, displayValues, onToggle, controllerDateRemoval.handleRemoveDate],
  );

  return (
    <>
      <Field
        ref={ref}
        ComponentProps={{
          Button: { 'data-cy': 'date-trigger', 'data-testid': 'date-trigger' },
          Container: { 'data-cy': 'date', 'data-testid': 'date' },
        }}
        IconSrc={<SolidCalendarIcon size={24} color={colors.secondary[500]} />}
        ValueComponent={DateValueComponent}
        hideArrow={isDisabled || currentValue.length > 0}
        isOpen={isOpen}
        isDisabled={isDisabled || (!isOpen && currentValue.length > 0)}
        title="Dates:"
        onToggle={onToggle}
      >
        <Center>
          <Typography data-testid="introduction" italic style={{ marginBottom: 20 }}>
            Click the calendar to choose the days you&apos;d like to post shifts on.
          </Typography>
          <DateContent
            defaultValues={fmtCurrentValueStr}
            // @ts-expect-error DateContent still written in Javascript
            excluded={excluded}
            onConfirm={handleConfirmDate}
            onLimitExceeded={controlsLimitExceeded.setOn}
          />
        </Center>
      </Field>

      {isShowLimitExceededWarning && (
        <ConfirmPopup
          Icon={() => (
            <div style={MaximumDateExceededPopupStyle.WarningIconWrapper}>
              <OutlineWarning01Icon width={80} height={80} color={colors.warning[500]} />
            </div>
          )}
          title="Sorry!"
          description="The maximum number of invites have been reached for this request form. You can send up to 10 shift invites at a time."
          rightButtonText="Close"
          rightButtonAction={controlsLimitExceeded.setOff}
        />
      )}

      {controllerDateRemoval.isShowConfirmation && (
        <ConfirmPopup
          title="Remove Date"
          description={
            <>
              Are you sure you would like to remove{' '}
              <strong>
                {moment(controllerDateRemoval.context?.value).format('ddd, MMM DD, YYYY')}
              </strong>
              <br />
              from your request?
            </>
          }
          descriptionStyle={ConfirmationPopupStyle.Description}
          leftButtonText="Go Back"
          leftButtonStyle={ConfirmationPopupStyle.LeftButton}
          closePopup={controllerDateRemoval.handleDeclineRemoval}
          leftButtonAction={controllerDateRemoval.handleDeclineRemoval}
          rightButtonText="Remove"
          rightButtonAction={controllerDateRemoval.handleConfirmRemoval}
        />
      )}
    </>
  );
};

/**
 * A flexible date picker component that supports both controlled and uncontrolled patterns.
 * Allows users to select multiple dates with a collapsible interface and confirmation dialogs.
 *
 * The component automatically detects whether to operate in controlled or uncontrolled mode
 * based on whether the `value` prop is provided.
 *
 * **Features:**
 * - Multi-date selection with calendar interface
 * - Controlled and uncontrolled patterns
 * - Date removal with confirmation dialogs
 * - Maximum date limit with warning (10 dates)
 * - Collapsible UI with Field wrapper
 * - forwardRef support for DOM access
 *
 * @example
 * ```tsx
 * // Uncontrolled mode (manages its own state)
 * <Date
 *   isOpen={isOpen}
 *   onChange={(dates) => console.log('Selected:', dates)}
 *   onToggle={() => setIsOpen(!isOpen)}
 * />
 *
 * // Uncontrolled with initial dates
 * <Date
 *   defaultValue={[new Date(), new Date(Date.now() + 86400000)]}
 *   onChange={handleDateChange}
 * />
 *
 * // Controlled mode (parent manages state)
 * const [selectedDates, setSelectedDates] = useState<Date[]>([]);
 * <Date
 *   value={selectedDates}
 *   onChange={setSelectedDates}
 *   isOpen={isOpen}
 *   onToggle={() => setIsOpen(!isOpen)}
 * />
 *
 * // With ref for DOM access
 * const dateRef = useRef<HTMLDivElement>(null);
 * <Date
 *   ref={dateRef}
 *   value={dates}
 *   onChange={setDates}
 * />
 * ```
 *
 * @remarks
 * - Component will warn in development if switching between controlled/uncontrolled modes
 * - Maximum of 10 dates can be selected at once
 * - Date removal requires user confirmation
 * - Uses moment.js for date formatting (format: 'ddd, MMM DD, YYYY')
 *
 * @param props - Component props (see {@link IDateProps})
 * @param ref - Optional ref to the root Field container element
 * @returns JSX element representing the date picker component
 */
export const Date = memo(forwardRef(Component)) as <T = HTMLDivElement>(
  props: IDateProps & { ref?: Ref<T> },
) => ReturnType<typeof Component>;
